/**
 * Ctextfx — agnostic character text entrance (multi-origin decrypt).
 * Use for hero copy and as a loading → settled reveal over plain or markdown text.
 */

"use client";

import type { CSSProperties, ReactElement } from "react";
import { memo, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useReducedMotion } from "framer-motion";
import {
  CtextfxVariant,
  DEFAULT_CTEXTFX_CHARS_PER_SECOND,
  DEFAULT_CTEXTFX_DECRYPT_GLYPHS,
  DEFAULT_CTEXTFX_SCRAMBLE_MAX_MS,
  DEFAULT_CTEXTFX_SCRAMBLE_MIN_MS,
} from "./Ctextfx.constants";
import {
  buildDecryptRevealed,
  maxDecryptRadius,
  pickDecryptOrigins,
  prefixRevealed,
} from "./Ctextfx.decrypt";
import { useCtextfxDecryptRadius } from "./Ctextfx.hooks";
import { randomScrambleGlyph } from "./Ctextfx.scramble-random";
import { registerScrambleGlyph } from "./Ctextfx.scramble-scheduler";

const DEFAULT_UNRESOLVED_STYLE: CSSProperties = {
  opacity: 0.55,
  color: "rgba(255,255,255,0.7)",
  WebkitTextFillColor: "rgba(255,255,255,0.7)",
  backgroundImage: "none",
};

const DECRYPT_RESERVE_WRAP_STYLE: CSSProperties = {
  position: "relative",
  display: "inline-block",
};

const DECRYPT_RESERVE_REAL_STYLE: CSSProperties = {
  visibility: "hidden",
};

const HIDDEN_GLYPH_STYLE: CSSProperties = { opacity: 0 };

function isPreservedScrambleChar(char: string): boolean {
  return char === " " || char === "\n" || char === "\t";
}

function isFrontierIndex({
  index,
  revealed,
}: {
  index: number;
  revealed: readonly boolean[];
}): boolean {
  if (revealed[index]) return false;
  const leftRevealed = index > 0 && revealed[index - 1] === true;
  const rightRevealed =
    index < revealed.length - 1 && revealed[index + 1] === true;
  return leftRevealed || rightRevealed;
}

function shouldScrambleIndex({
  index,
  char,
  revealed,
  scrambleUnresolved,
}: {
  index: number;
  char: string;
  revealed: readonly boolean[];
  scrambleUnresolved: boolean;
}): boolean {
  if (revealed[index] === true || isPreservedScrambleChar(char)) return false;
  return scrambleUnresolved || isFrontierIndex({ index, revealed });
}

interface InitialScrambleGlyphsInput {
  chars: readonly string[];
  decryptGlyphs: string;
}

/** Seeds every potentially scrambled glyph from text/alphabet identity alone —
 *  never from mask identity, so a mask change cannot reshuffle survivors. */
function initialScrambleGlyphs({
  chars,
  decryptGlyphs,
}: InitialScrambleGlyphsInput): string[] {
  const next: string[] = [];
  for (let index = 0; index < chars.length; index++) {
    const char = chars[index] ?? "";
    if (isPreservedScrambleChar(char)) {
      next.push(char);
      continue;
    }
    const exclude = new Set<string>();
    const leftGlyph = next[index - 1];
    if (leftGlyph && !isPreservedScrambleChar(leftGlyph)) exclude.add(leftGlyph);
    next.push(randomScrambleGlyph({ glyphs: decryptGlyphs, exclude }));
  }
  return next;
}

/** Per-text scramble identity: the first-paint glyphs plus the live registry
 *  of mounted glyph nodes their neighbours are read from. */
interface ScrambleSeed {
  initialGlyphs: string[];
  registry: Map<number, HTMLElement>;
}

interface GetNeighborGlyphsInput {
  glyphRegistry: ReadonlyMap<number, HTMLElement>;
  index: number;
}

/** Live adjacent glyphs, read from the DOM so the scheduler never needs a
 *  React snapshot of the row. */
function getNeighborGlyphs({
  glyphRegistry,
  index,
}: GetNeighborGlyphsInput): ReadonlySet<string> {
  const glyphs = new Set<string>();
  const left = glyphRegistry.get(index - 1)?.textContent;
  const right = glyphRegistry.get(index + 1)?.textContent;
  if (left != null && !isPreservedScrambleChar(left)) glyphs.add(left);
  if (right != null && !isPreservedScrambleChar(right)) glyphs.add(right);
  return glyphs;
}

interface ScrambleGlyphNodeProps {
  glyphRegistry: Map<number, HTMLElement>;
  glyphs: string;
  index: number;
  initialGlyph: string;
  maxDelayMs: number;
  minDelayMs: number;
  style: CSSProperties;
}

/** One stable, childless span per unresolved character. React commits its
 *  style/identity; the shared scheduler owns its text node, so a scramble tick
 *  costs one DOM write and zero renders. */
function ScrambleGlyphNode({
  glyphRegistry,
  glyphs,
  index,
  initialGlyph,
  maxDelayMs,
  minDelayMs,
  style,
}: ScrambleGlyphNodeProps): ReactElement {
  const nodeRef = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (node == null) return;
    node.textContent = initialGlyph;
    glyphRegistry.set(index, node);
    return (): void => {
      glyphRegistry.delete(index);
    };
  }, [glyphRegistry, index, initialGlyph]);
  useEffect(() => {
    const node = nodeRef.current;
    if (node == null) return;
    return registerScrambleGlyph({
      getExcludedGlyphs: () => getNeighborGlyphs({ glyphRegistry, index }),
      glyphs,
      maxDelayMs,
      minDelayMs,
      node,
    });
  }, [glyphRegistry, glyphs, index, maxDelayMs, minDelayMs]);
  return (
    <span
      ref={nodeRef}
      aria-hidden
      style={style}
    />
  );
}

interface RevealCharProps {
  char: string;
  glyphRegistry: Map<number, HTMLElement>;
  glyphs: string;
  index: number;
  initialGlyph: string;
  isRevealed: boolean;
  reserveScrambleStyle: CSSProperties;
  reserveWidth: boolean;
  scrambleMaxMs: number;
  scrambleMinMs: number;
  showScramble: boolean;
  unresolvedStyle: CSSProperties;
}

/** One character cell, memoized on primitives plus stable style/attribute
 *  identities. The reveal radius advances ~90 characters a second, so without
 *  this every tick re-rendered every cell in the text — three nodes each under
 *  `reserveWidth`. Now a tick only re-renders the cells that actually crossed
 *  the frontier; the rest bail out before touching the DOM. */
const RevealChar = memo(function RevealChar({
  char,
  glyphRegistry,
  glyphs,
  index,
  initialGlyph,
  isRevealed,
  reserveScrambleStyle,
  reserveWidth,
  scrambleMaxMs,
  scrambleMinMs,
  showScramble,
  unresolvedStyle,
}: RevealCharProps): ReactElement {
  if (isRevealed) {
    return <span>{char}</span>;
  }
  if (showScramble && reserveWidth) {
    return (
      <span style={DECRYPT_RESERVE_WRAP_STYLE}>
        <span aria-hidden style={DECRYPT_RESERVE_REAL_STYLE}>
          {char}
        </span>
        <ScrambleGlyphNode
          glyphRegistry={glyphRegistry}
          glyphs={glyphs}
          index={index}
          initialGlyph={initialGlyph}
          maxDelayMs={scrambleMaxMs}
          minDelayMs={scrambleMinMs}
          style={reserveScrambleStyle}
        />
      </span>
    );
  }
  if (showScramble) {
    return (
      <ScrambleGlyphNode
        glyphRegistry={glyphRegistry}
        glyphs={glyphs}
        index={index}
        initialGlyph={initialGlyph}
        maxDelayMs={scrambleMaxMs}
        minDelayMs={scrambleMinMs}
        style={unresolvedStyle}
      />
    );
  }
  return (
    <span aria-hidden style={HIDDEN_GLYPH_STYLE}>
      {char}
    </span>
  );
});

function CharReveal({
  text,
  revealed,
  scrambleVisible,
  decrypt,
  play,
  scrambleUnresolved,
  reserveWidth,
  unresolvedStyle,
  decryptGlyphs,
  scrambleMinMs,
  scrambleMaxMs,
  style,
}: {
  text: string;
  revealed: readonly boolean[];
  scrambleVisible?: readonly boolean[];
  decrypt: boolean;
  play: boolean;
  scrambleUnresolved: boolean;
  reserveWidth: boolean;
  unresolvedStyle: CSSProperties;
  decryptGlyphs: string;
  scrambleMinMs: number;
  scrambleMaxMs: number;
  style?: CSSProperties;
}): ReactElement {
  const chars = useMemo(() => Array.from(text), [text]);
  // Seed and live-node registry share one text/alphabet lifetime: an
  // equal-mask rerender or a one-index reveal must never reshuffle the glyphs
  // that survive it.
  const scrambleSeed = useMemo<ScrambleSeed>(
    () => ({
      initialGlyphs: initialScrambleGlyphs({ chars, decryptGlyphs }),
      registry: new Map<number, HTMLElement>(),
    }),
    [chars, decryptGlyphs],
  );
  // Every identity a cell reads must survive a radius tick unchanged, or the
  // memo below never bails out.
  const reserveScrambleStyle = useMemo<CSSProperties>(
    () => ({
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      ...unresolvedStyle,
    }),
    [unresolvedStyle],
  );
  return (
    <span
      style={style}
    >
      {chars.map((char, index) => {
        const isRevealed = revealed[index] === true;
        const showScramble =
          decrypt &&
          play &&
          (scrambleVisible?.[index] ?? true) &&
          shouldScrambleIndex({ index, char, revealed, scrambleUnresolved });
        return (
          <RevealChar
            key={index}
            char={char}
            glyphRegistry={scrambleSeed.registry}
            glyphs={decryptGlyphs}
            index={index}
            initialGlyph={scrambleSeed.initialGlyphs[index] ?? char}
            isRevealed={isRevealed}
            reserveScrambleStyle={reserveScrambleStyle}
            reserveWidth={reserveWidth}
            scrambleMaxMs={scrambleMaxMs}
            scrambleMinMs={scrambleMinMs}
            showScramble={showScramble}
            unresolvedStyle={unresolvedStyle}
          />
        );
      })}
    </span>
  );
}

export interface CtextfxProps {
  text: string;
  variant: CtextfxVariant;
  /** Changing this replays the entrance. */
  playKey: string;
  /** Hold the reveal until the text is actually visible (defaults to true). */
  play?: boolean;
  /** Decrypt only: keep unresolved letters visible as faded random glyphs. */
  scrambleUnresolved?: boolean;
  /**
   * Reserve each letter's real width under the scramble (exact wrap/spacing).
   * Do not use inside gradient-clipped text — absolute copies fall out of the clip.
   */
  reserveWidth?: boolean;
  /** Drive the reveal mask from outside (e.g. one clock across markdown paragraphs). */
  controlledRevealed?: readonly boolean[];
  /** Decrypt only: optionally hide unresolved glyphs outside this external mask. */
  controlledScrambleVisible?: readonly boolean[];
  /** Type / Decrypt throughput. */
  charsPerSecond?: number;
  /** Glyph alphabet for Decrypt scramble. */
  decryptGlyphs?: string;
  scrambleMinMs?: number;
  scrambleMaxMs?: number;
  /** Paint for unresolved Decrypt glyphs (defaults to faded light fill). */
  unresolvedStyle?: CSSProperties;
  /** Fires once when the character reveal finishes (or immediately if it doesn't animate). */
  onSettled?: () => void;
  style?: CSSProperties;
}

export function Ctextfx({
  text,
  variant,
  playKey,
  play = true,
  scrambleUnresolved = false,
  reserveWidth = false,
  controlledRevealed,
  controlledScrambleVisible,
  charsPerSecond = DEFAULT_CTEXTFX_CHARS_PER_SECOND,
  decryptGlyphs = DEFAULT_CTEXTFX_DECRYPT_GLYPHS,
  scrambleMinMs = DEFAULT_CTEXTFX_SCRAMBLE_MIN_MS,
  scrambleMaxMs = DEFAULT_CTEXTFX_SCRAMBLE_MAX_MS,
  unresolvedStyle = DEFAULT_UNRESOLVED_STYLE,
  onSettled,
  style,
}: CtextfxProps): ReactElement {
  const shouldReduceMotion = useReducedMotion() === true;
  const isDecrypt = variant === CtextfxVariant.Decrypt;
  const animatesChars = !shouldReduceMotion && isDecrypt;
  const length = Array.from(text).length;
  const ownsReveal = animatesChars && controlledRevealed == null;
  const origins = useMemo(
    () => (ownsReveal ? pickDecryptOrigins({ length, playKey }) : []),
    [ownsReveal, length, playKey],
  );
  const maxRadius = useMemo(
    () => maxDecryptRadius({ length, origins }),
    [length, origins],
  );
  const decryptRadius = useCtextfxDecryptRadius({
    maxRadius,
    active: ownsReveal,
    play,
    playKey,
    charsPerSecond,
  });
  const internalRevealed = useMemo(() => {
    if (!ownsReveal) return prefixRevealed(length, length);
    return buildDecryptRevealed({ length, origins, radius: decryptRadius });
  }, [ownsReveal, length, origins, decryptRadius]);
  const revealed = controlledRevealed ?? internalRevealed;
  const settled = revealed.length === length && revealed.every(Boolean);
  const settledOnceRef = useRef(false);
  useEffect(() => {
    settledOnceRef.current = false;
  }, [playKey, text]);
  useEffect(() => {
    if (!onSettled || settledOnceRef.current) return;
    const done =
      !animatesChars || shouldReduceMotion || (play && settled);
    if (!done) return;
    settledOnceRef.current = true;
    onSettled();
  }, [onSettled, animatesChars, shouldReduceMotion, play, settled]);

  if (shouldReduceMotion) {
    return (
      <span
        key={playKey}
        style={style}
      >
        {text}
      </span>
    );
  }

  if (animatesChars) {
    return (
      <CharReveal
        key={playKey}
        text={text}
        revealed={revealed}
        scrambleVisible={controlledScrambleVisible}
        decrypt={isDecrypt}
        play={play}
        scrambleUnresolved={scrambleUnresolved}
        reserveWidth={reserveWidth}
        unresolvedStyle={unresolvedStyle}
        decryptGlyphs={decryptGlyphs}
        scrambleMinMs={scrambleMinMs}
        scrambleMaxMs={scrambleMaxMs}
        style={style}
      />
    );
  }

  return (
    <span
      key={`${playKey}-${play}`}
      style={style}
    >
      {text}
    </span>
  );
}
